import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  MoreVert,
  Search,
  FilterList,
  CheckCircle,
  Warning,
  Block,
  Store,
  Email,
  Phone,
  LocationOn,
  Star,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { API_ENDPOINTS, apiRequest } from '../../utils/api';

interface Vendor {
  id: string;
  businessName: string;
  ownerName: string;
  email: string;
  phone: string;
  category: string;
  status: 'pending' | 'approved' | 'rejected';
  businessType: string;
  location: {
    address: string;
    city: string;
    state: string;
  };
  rating: {
    average: number;
    count: number;
    userRating: number | null;
  };
  isVerified: boolean;
  isActive: boolean;
  subscriptionPlan: string;
  createdAt: string;
  offersCount: number;
}

const DEFAULT_RATING: Vendor['rating'] = {
  average: 0,
  count: 0,
  userRating: null,
};

const VendorManagement: React.FC = () => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [filteredVendors, setFilteredVendors] = useState<Vendor[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'suspend' | 'activate'>('approve');
  const [isLoading, setIsLoading] = useState(true);

  const formatAverage = (value: number) => (Number.isFinite(value) ? value.toFixed(1) : '0.0');
  const formatCount = (value: number) => new Intl.NumberFormat().format(Number.isFinite(value) ? value : 0);

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchRatingSummary = async (storeId: string, fallbackRating: Vendor['rating']) => {
    const normalizedFallback: Vendor['rating'] = { ...fallbackRating };

    try {
      const response = await apiRequest(API_ENDPOINTS.STORE_RATING_SUMMARY(storeId));

      if (response?.success && response?.data) {
        const { average, count, userRating } = response.data;

        return {
          average: typeof average === 'number' && Number.isFinite(average) ? average : normalizedFallback.average,
          count: typeof count === 'number' && Number.isFinite(count) ? count : normalizedFallback.count,
          userRating: typeof userRating === 'number' && Number.isFinite(userRating) ? userRating : null,
        };
      }
    } catch (error) {
      console.error(`Error fetching rating summary for store ${storeId}:`, error);
    }

    return normalizedFallback;
  };

  const filterVendors = useCallback(() => {
    let filtered = vendors;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(vendor =>
        vendor.businessName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.ownerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(vendor => vendor.status === statusFilter);
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(vendor => vendor.category === categoryFilter);
    }

    setFilteredVendors(filtered);
  }, [vendors, searchTerm, statusFilter, categoryFilter]);

  useEffect(() => {
    filterVendors();
  }, [filterVendors]);

  const fetchVendors = async () => {
    try {
      setIsLoading(true);
      const data = await apiRequest(API_ENDPOINTS.VENDORS);

      if (data.success) {
        const vendorsPayload = data.data?.vendors || [];

        const transformedVendors: Vendor[] = await Promise.all(
          vendorsPayload.map(async (vendor: any) => {
            const fallbackRating: Vendor['rating'] = {
              average: typeof vendor.rating?.average === 'number' ? vendor.rating.average : DEFAULT_RATING.average,
              count: typeof vendor.rating?.count === 'number' ? vendor.rating.count : DEFAULT_RATING.count,
              userRating: typeof vendor.rating?.userRating === 'number' ? vendor.rating.userRating : DEFAULT_RATING.userRating,
            };

            const ratingSummary = await fetchRatingSummary(vendor._id, fallbackRating);

            return {
              id: vendor._id,
              businessName: vendor.businessName,
              ownerName: vendor.ownerName,
              email: vendor.email,
              phone: vendor.phone,
              category: vendor.category?.name || 'Unknown',
              status: vendor.status,
              businessType: vendor.businessType,
              location: {
                address: vendor.location?.address || vendor.address?.street || '',
                city: vendor.location?.city || vendor.address?.city || '',
                state: vendor.address?.state || '',
              },
              rating: ratingSummary,
              isVerified: vendor.isVerified,
              isActive: vendor.isActive,
              subscriptionPlan: vendor.subscriptionPlan,
              createdAt: vendor.createdAt,
              offersCount: 0, // Will need to fetch separately or add to vendor query
            };
          })
        );

        setVendors(transformedVendors);
        setSelectedVendor((prev) => {
          if (prev) {
            return transformedVendors.find((vendor) => vendor.id === prev.id) || transformedVendors[0] || null;
          }
          return transformedVendors[0] || null;
        });
      } else {
        // Fallback to mock data if API fails
        const mockVendors: Vendor[] = [
          {
            id: '1',
            businessName: 'Pizza Palace',
            ownerName: 'John Smith',
            email: 'john@pizzapalace.com',
            phone: '+1 (555) 123-4567',
            category: 'Restaurant',
            status: 'approved',
            businessType: 'restaurant',
            location: {
              address: '123 Main St',
              city: 'New York',
              state: 'NY',
            },
            rating: {
              average: 4.5,
              count: 120,
              userRating: null,
            },
            isVerified: true,
            isActive: true,
            subscriptionPlan: 'premium',
            createdAt: '2024-01-01T00:00:00Z',
            offersCount: 15,
          },
        ];
        setVendors(mockVendors);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setVendors([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVendorAction = (vendor: Vendor, action: 'approve' | 'reject' | 'suspend' | 'activate') => {
    setSelectedVendor(vendor);
    setActionType(action);
    setConfirmDialog(true);
    setActionMenuAnchor(null);
  };

  const confirmAction = async () => {
    if (!selectedVendor) return;

    try {
      let endpoint = '';
      let method = 'PUT';

      const baseUrl = process.env.REACT_APP_ADMIN_URL || 'https://godiscountdays.com/api/admin';
      
      switch (actionType) {
        case 'approve':
          endpoint = `${baseUrl}/vendors/${selectedVendor.id}/approve`;
          break;
        case 'reject':
          endpoint = `${baseUrl}/vendors/${selectedVendor.id}/reject`;
          break;
        case 'suspend':
          endpoint = `${baseUrl}/vendors/${selectedVendor.id}/suspend`;
          break;
        case 'activate':
          endpoint = `${baseUrl}/vendors/${selectedVendor.id}/activate`;
          break;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(endpoint, { 
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        // Update local state
        setVendors(vendors.map(vendor =>
          vendor.id === selectedVendor.id
            ? { 
                ...vendor, 
                status: actionType === 'approve' ? 'approved' : 
                       actionType === 'reject' ? 'rejected' : vendor.status,
                isActive: actionType === 'activate' ? true : 
                         actionType === 'suspend' ? false : vendor.isActive
              }
            : vendor
        ));
      }
    } catch (error) {
      console.error('Error performing action:', error);
    } finally {
      setConfirmDialog(false);
      setSelectedVendor(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle />;
      case 'pending':
        return <Warning />;
      case 'rejected':
        return <Block />;
      default:
        return <Warning />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const columns: GridColDef[] = [
    {
      field: 'businessName',
      headerName: 'Business',
      width: 200,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ mr: 2, width: 40, height: 40, bgcolor: 'primary.light' }}>
            <Store />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {params.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.ownerName}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Contact',
      width: 180,
      minWidth: 160,
      renderCell: (params) => (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Email sx={{ mr: 1, fontSize: 16 }} />
            <Typography variant="caption">{params.value}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Phone sx={{ mr: 1, fontSize: 16 }} />
            <Typography variant="caption">{params.row.phone}</Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 150,
      minWidth: 130,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LocationOn sx={{ mr: 1, fontSize: 16 }} />
          <Typography variant="caption">
            {params.value.city}, {params.value.state}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 120,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          icon={getStatusIcon(params.value)}
          label={params.value.charAt(0).toUpperCase() + params.value.slice(1)}
          color={getStatusColor(params.value) as any}
          size="small"
        />
      ),
    },
    {
      field: 'rating',
      headerName: 'Rating',
      width: 110,
      renderCell: (params) => {
        const ratingValue: Vendor['rating'] | undefined = params.value;

        if (!ratingValue) {
          return (
            <Typography variant="caption" color="text.secondary">
              N/A
            </Typography>
          );
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Star sx={{ mr: 0.5, fontSize: 16, color: 'orange' }} />
            <Typography variant="caption">
              {formatAverage(ratingValue.average)} ({formatCount(ratingValue.count)})
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'offersCount',
      headerName: 'Offers',
      width: 80,
      type: 'number',
    },
    {
      field: 'subscriptionPlan',
      headerName: 'Plan',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'premium' ? 'primary' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Joined',
      width: 120,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<MoreVert />}
          label="More"
          onClick={(event) => {
            setActionMenuAnchor(event.currentTarget);
            setSelectedVendor(params.row);
          }}
        />,
      ],
    },
  ];

  const activeVendor = selectedVendor || (filteredVendors.length > 0 ? filteredVendors[0] : null);

  return (
    <Box>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom
        sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
      >
        Vendor Management
      </Typography>
      <Typography 
        variant="body1" 
        color="text.secondary" 
        sx={{ 
          mb: { xs: 3, md: 4 },
          fontSize: { xs: '0.875rem', sm: '1rem' },
        }}
      >
        Manage and monitor all registered vendors on the platform.
      </Typography>

      {/* Filters */}
      <Card sx={{ 
        mb: 3, 
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0',
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 2 }, alignItems: 'center' }}>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 250px' }, minWidth: { xs: '100%', sm: '250px' } }}>
              <TextField
                fullWidth
                placeholder="Search vendors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 150px' }, minWidth: { xs: 'calc(50% - 8px)', sm: '150px' } }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 150px' }, minWidth: { xs: 'calc(50% - 8px)', sm: '150px' } }}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  <MenuItem value="Restaurant">Restaurant</MenuItem>
                  <MenuItem value="Retail">Retail</MenuItem>
                  <MenuItem value="Service">Service</MenuItem>
                  <MenuItem value="Entertainment">Entertainment</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 150px' }, minWidth: { xs: 'calc(50% - 8px)', sm: '150px' } }}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                fullWidth
              >
                More Filters
              </Button>
            </Box>
            <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 200px' }, minWidth: { xs: 'calc(50% - 8px)', sm: '200px' } }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" size="small" sx={{ flex: 1 }}>
                  Export
                </Button>
                <Button variant="outlined" size="small" sx={{ flex: 1 }}>
                  Bulk Actions
                </Button>
              </Box>
            </Box>
            </Box>
        </CardContent>
      </Card>

      {/* Store Rating Summary */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e0e0e0',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Store Rating Summary
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {activeVendor
                  ? `Ratings overview for ${activeVendor.businessName}`
                  : 'Select a vendor from the table to view their store ratings.'}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #FF9800 0%, #FFC107 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Star sx={{ color: 'white', fontSize: 28 }} />
            </Box>
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
              <CircularProgress size={32} />
            </Box>
          ) : activeVendor ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mt: 4 }}>
              <Box>
                <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
                  {formatAverage(activeVendor.rating.average)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average Rating
                </Typography>
              </Box>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {formatCount(activeVendor.rating.count)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Reviews
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ mt: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No vendor data available.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Vendors Table */}
      <Card sx={{ 
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0',
        overflow: 'hidden',
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box 
            sx={{ 
              height: 600, 
              width: '100%',
              overflowX: 'auto',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                height: '8px',
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: '#555',
                },
              },
            }}
          >
            <Box sx={{ minWidth: { xs: '800px', md: '100%' } }}>
              <DataGrid
              rows={filteredVendors}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 },
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              checkboxSelection
              disableRowSelectionOnClick
              loading={isLoading}
              onRowClick={(params) => setSelectedVendor(params.row as Vendor)}
              sx={{
                border: 'none',
                '& .MuiDataGrid-root': {
                  border: 'none',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #e9ecef',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: '#495057',
                  height: '56px',
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  },
                  '& .MuiDataGrid-columnHeaderTitleContainer': {
                    padding: '0 16px',
                  },
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #f1f3f4',
                  padding: '0 16px',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#495057',
                  height: '72px',
                  minHeight: '72px !important',
                  '& .MuiBox-root': {
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    gap: '8px',
                  },
                },
                '& .MuiDataGrid-row': {
                  display: 'flex',
                  alignItems: 'stretch',
                  minHeight: '72px !important',
                  '&:nth-of-type(even)': {
                    backgroundColor: '#fafbfc',
                  },
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#e3f2fd !important',
                  transform: 'scale(1.001)',
                  transition: 'all 0.2s ease-in-out',
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '1px solid #e9ecef',
                  padding: '16px 20px',
                  backgroundColor: '#f8f9fa',
                  minHeight: '64px',
                },
                '& .MuiDataGrid-selectedRowCount': {
                  color: '#3B3B3B',
                  fontWeight: 500,
                },
                '& .MuiTablePagination-root': {
                  color: '#495057',
                },
                '& .MuiCheckbox-root': {
                  color: '#3B3B3B',
                  padding: '8px',
                },
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
                '& .MuiDataGrid-iconButtonContainer': {
                  visibility: 'visible',
                },
                '& .MuiDataGrid-sortIcon': {
                  opacity: 0.6,
                },
                '& .MuiDataGrid-columnHeader': {
                  '&:focus': {
                    outline: 'none',
                  },
                },
              }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => setActionMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => handleVendorAction(selectedVendor!, 'approve')}
          disabled={selectedVendor?.status === 'approved'}
        >
          <CheckCircle sx={{ mr: 1 }} />
          Approve
        </MenuItem>
        <MenuItem
          onClick={() => handleVendorAction(selectedVendor!, 'reject')}
          disabled={selectedVendor?.status === 'rejected'}
        >
          <Block sx={{ mr: 1 }} />
          Reject
        </MenuItem>
        <MenuItem
          onClick={() => handleVendorAction(selectedVendor!, 'suspend')}
          disabled={!selectedVendor?.isActive}
        >
          <Warning sx={{ mr: 1 }} />
          Suspend
        </MenuItem>
        <MenuItem
          onClick={() => handleVendorAction(selectedVendor!, 'activate')}
          disabled={selectedVendor?.isActive}
        >
          <CheckCircle sx={{ mr: 1 }} />
          Activate
        </MenuItem>
      </Menu>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>
          Confirm Action
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {actionType} vendor "{selectedVendor?.businessName}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>
            Cancel
          </Button>
          <Button onClick={confirmAction} color="primary" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorManagement;
